import React from 'react';

import { Header } from '@rbilabs/universal-components';
import { HeaderProps } from 'ucl/components/header/Header';
import { Typography } from 'ucl/components/text/Typography';

import { HorizontalPosition, IColorable, IHorizontalPositionable } from './types';

const horizontalPositionToTextAlignMap: {
  [key: string]: 'left' | 'center' | 'right';
} = {
  [HorizontalPosition.LEFT]: 'left',
  [HorizontalPosition.CENTER]: 'center',
  [HorizontalPosition.RIGHT]: 'right',
};

type ColorableAndHorizontalPositionable = IColorable & IHorizontalPositionable;

const baseHeadlineStyles: HeaderProps = {
  fontSize: '5xl',
  lineHeight: 'xs',
};

export const Headline1 = Header.withConfig<ColorableAndHorizontalPositionable>(
  ({ color, horizontalPosition }) => {
    return {
      ...baseHeadlineStyles,
      accessibilityLevel: 1,
      fontSize: '8xl',
      textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
      color,
    };
  }
);

export const Headline2 = Header.withConfig<ColorableAndHorizontalPositionable>(
  ({ color, horizontalPosition }) => ({
    ...baseHeadlineStyles,
    accessibilityLevel: 2,
    fontSize: '6xl',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
  })
);

export const Headline3 = Header.withConfig<ColorableAndHorizontalPositionable>(
  ({ color, horizontalPosition }) => ({
    accessibilityLevel: 3,
    ...baseHeadlineStyles,
    fontSize: '5xl',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
  })
);

export const Headline4 = Header.withConfig<ColorableAndHorizontalPositionable>(
  ({ color, horizontalPosition }) => ({
    ...baseHeadlineStyles,
    accessibilityLevel: 4,
    fontSize: '4xl',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
  })
);

export const Headline5 = Header.withConfig<ColorableAndHorizontalPositionable>(
  ({ color, horizontalPosition }) => ({
    ...baseHeadlineStyles,
    accessibilityLevel: 5,
    fontSize: '2xl',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
  })
);

export const Headline6 = Header.withConfig<ColorableAndHorizontalPositionable>(
  ({ color, horizontalPosition }) => ({
    ...baseHeadlineStyles,
    accessibilityLevel: 6,
    fontSize: 'xl',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
  })
);

export const Subtitle1 = ({
  color,
  horizontalPosition,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & ColorableAndHorizontalPositionable) =>
  React.createElement(Typography, {
    fontSize: 'lg',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
    ...props,
  });

export const Subtitle2 = ({
  color,
  horizontalPosition,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & ColorableAndHorizontalPositionable) =>
  React.createElement(Typography, {
    fontSize: 'sm',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
    ...props,
  });

export const Normal = ({
  color,
  horizontalPosition,
  fontSize,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & ColorableAndHorizontalPositionable) =>
  React.createElement(Typography, {
    fontSize: fontSize ?? 'sm',
    lineHeight: 24,
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
    ...props,
  });

export const Body1 = ({
  color,
  horizontalPosition,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & ColorableAndHorizontalPositionable) =>
  React.createElement(Typography, {
    fontSize: 'sm',
    lineHeight: 24,
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
    ...props,
  });

export const Body2 = ({
  color,
  horizontalPosition,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & ColorableAndHorizontalPositionable) =>
  React.createElement(Typography, {
    fontSize: 'xs',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
    ...props,
  });

export const Caption = ({
  color,
  horizontalPosition,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & ColorableAndHorizontalPositionable) =>
  React.createElement(Typography, {
    fontSize: 'xs',
    textAlign: horizontalPositionToTextAlignMap[horizontalPosition],
    color,
    ...props,
  });
